/** @jsx h */
import { h, Component } from 'preact';

const dot = (v1, v2) => (v1.x * v2.x) + (v1.y * v2.y);
const getLen = v => Math.sqrt(dot(v, v));

export default class Page extends Component {
  constructor(props) {
    super(props);
    this.active = {};
    this.state = {
      offsetX: 0,
      offsetY: 0,
      zoom: 1,
      cursorX: 50,
      cursorY: 50,
      canDraw: false,
      pathID: null,
      textInput: '',
      textID: null,
    };
    this.isTouchDevice = (('ontouchstart' in window)
                          || (window.DocumentTouch && document instanceof window.DocumentTouch));
    this.touch = {
      pathID: null,
      preV: { x: null, y: null },
      pinchStartLen: null,
      scale: 1,
      isDoubleTap: false,
      delta: null,
      last: null,
      now: null,
      end: null,
      multiTouch: false,
      tapTimeout: null,
      longTapTimeout: null,
      singleTapTimeout: null,
      swipeTimeout: null,
      x1: null,
      x2: null,
      y1: null,
      y2: null,
      pinchStart: { x: null, y: null },
      preTapPosition: { x: null, y: null },
      afterLongTap: false,
      afterLongTapTimeout: null,
    };
  }

  componentDidMount() {
    this.svgEl.focus();
  }

  onMouseDown({ x, y, e }) {
    if (e.buttons === 1) {
      if (e.ctrlKey || e.shiftKey || e.metaKey) {
        this.drag = {
          x: e.clientX,
          y: e.clientY,
          offsetX: this.state.offsetX,
          offsetY: this.state.offsetY,
        };
      } else {
        const id = this.props.pathStart({ x, y });
        this.setState({ canDraw: true, pathID: id });
      }
    } else if (e.buttons === 4) {
      this.deleteActive();
    }
  }

  onMouseUp() {
    if (this.state.pathID) {
      this.props.pathEnd(this.state.pathID);
      this.setState({ canDraw: false, pathID: null });
    }
    if (this.drag) {
      this.drag = undefined;
    }
  }

  onMouseMove({ x, y, e }) {
    this.isTouchDevice = false;
    if (this.drag) {
      const dx = e.clientX - this.drag.x;
      const dy = e.clientY - this.drag.y;
      this.setState({ offsetX: this.drag.offsetX + dx, offsetY: this.drag.offsetY + dy });
      return;
    }
    if (this.state.canDraw) {
      this.props.pathAdd({ id: this.state.pathID, x, y });
    }
    if (this.state.textID) {
      if (new Date() - this.state.lastKeyPressTime < 300) {
        return;
      }
      this.textID = null;
      this.textInput = '';
      this.props.textEnd();
    }
    this.setState({
      cursorX: x,
      cursorY: y,
      textID: null,
      textInput: '',
    });
  }

  onTouchStart(e) {
    if (!e.touches) return;
    this.isTouchDevice = true;
    this.touch.now = Date.now();
    this.touch.x1 = e.touches[0].clientX;
    this.touch.y1 = e.touches[0].clientY;
    this.touch.scale = this.state.zoom;
    this.touch.delta = this.touch.now - (this.touch.last || this.touch.now);
    if (this.touch.preTapPosition.x !== null) {
      this.touch.isDoubleTap = (this.touch.delta > 0 && this.touch.delta <= 250
         && Math.abs(this.touch.preTapPosition.x - this.touch.x1) < 30
         && Math.abs(this.touch.preTapPosition.y - this.touch.y1) < 30);
    }
    this.touch.preTapPosition.x = this.touch.x1;
    this.touch.preTapPosition.y = this.touch.y1;
    this.touch.last = this.touch.now;
    const { preV } = this.touch;
    const len = e.touches.length;
    if (len > 1) {
      clearTimeout(this.touch.longTapTimeout);
      clearTimeout(this.touch.singleTapTimeout);
      const v = {
        x: e.touches[1].clientX - this.touch.x1,
        y: e.touches[1].clientY - this.touch.y1,
      };
      preV.x = v.x;
      preV.y = v.y;
      this.touch.pinchStartLen = getLen(preV);
      this.touch.pinchStart = {
        x: (e.touches[1].clientX + this.touch.x1) / 2,
        y: (e.touches[1].clientY + this.touch.y1) / 2,
        offsetX: this.state.offsetX,
        offsetY: this.state.offsetY,
      };
    }
    this.touch.longTapTimeout = setTimeout(() => {
      const text = window.prompt('Enter text:'); // eslint-disable-line no-alert
      if (text) {
        const w = this.props.elements.measure(text);
        this.props.textCreate({
          x: ((this.touch.x1 - this.state.offsetX) / this.state.zoom) - (w / 2),
          y: (this.touch.y1 - this.state.offsetY) / this.state.zoom,
          text,
        });
      }
      this.touch.afterLongTap = true;
      this.touch.afterLongTapTimeout = setTimeout(() => {
        this.touch.afterLongTap = false;
      }, 1000);
    }, 750);
  }

  onTouchMove(e) {
    e.preventDefault();
    const { preV } = this.touch;
    const len = e.touches.length;
    const currentX = e.touches[0].clientX;
    const currentY = e.touches[0].clientY;
    this.touch.isDoubleTap = false;
    if (len > 1) {
      const v = {
        x: e.touches[1].clientX - currentX,
        y: e.touches[1].clientY - currentY,
      };
      if (preV.x !== null) {
        if (this.touch.pinchStartLen > 0) {
          const scale = (getLen(v) / this.touch.pinchStartLen) * this.touch.scale;
          const newZoom = Math.min(Math.max(scale, 0.2), 5);
          const center = {
            x: (e.touches[1].clientX + currentX) / 2,
            y: (e.touches[1].clientY + currentY) / 2,
          };
          const oldSVGCenter = {
            x: (this.touch.pinchStart.offsetX - this.touch.pinchStart.x) / this.touch.scale,
            y: (this.touch.pinchStart.offsetY - this.touch.pinchStart.y) / this.touch.scale,
          };
          this.setState({
            zoom: newZoom,
            offsetX: (oldSVGCenter.x * newZoom) + center.x,
            offsetY: (oldSVGCenter.y * newZoom) + center.y,
          });
        }
      }
      preV.x = v.x;
      preV.y = v.y;
      this.touch.multiTouch = true;
    } else {
      if (this.touch.x2 !== null) {
        e.deltaX = currentX - this.touch.x2;
        e.deltaY = currentY - this.touch.y2;
      } else {
        e.deltaX = 0;
        e.deltaY = 0;
      }
      if (!this.touch.pathID && this.touch.x1 !== null && this.touch.y1 !== null) {
        this.touch.pathID = this.props.pathStart({
          x: (this.touch.x1 - this.state.offsetX) / this.state.zoom,
          y: (this.touch.y1 - this.state.offsetY) / this.state.zoom,
        });
      }
      if (this.touch.pathID) {
        this.props.pathAdd({
          id: this.touch.pathID,
          x: (currentX - this.state.offsetX) / this.state.zoom,
          y: (currentY - this.state.offsetY) / this.state.zoom,
        });
      }
    }
    clearTimeout(this.touch.longTapTimeout);
    this.touch.x2 = currentX;
    this.touch.y2 = currentY;
    if (len > 1) {
      e.preventDefault();
    }
  }

  onTouchEnd(e) {
    e.preventDefault();
    this.touch.end = Date.now();
    clearTimeout(this.touch.longTapTimeout);
    e.origin = [this.touch.x1, this.touch.y1];
    if (this.touch.multiTouch === false) {
      if (this.touch.afterLongTap) {
        clearTimeout(this.touch.afterLongTapTimeout);
        this.touch.afterLongTap = false;
      } else {
        this.touch.tapTimeout = setTimeout(() => {
          if (this.touch.isDoubleTap) {
            clearTimeout(this.touch.singleTapTimeout);
            this.touch.isDoubleTap = false;
            const x = (e.origin[0] - this.state.offsetX) / this.state.zoom;
            const y = (e.origin[1] - this.state.offsetY) / this.state.zoom;
            const dist = 25 / this.state.zoom;
            this.props.elements.map((el, id) => {
              if (this.props.elements.nearby(x, y, id, dist)) {
                this.props.remove(id);
              }
              return null;
            });
          }
        }, 0);
      }
    }
    this.touch.preV.x = 0;
    this.touch.preV.y = 0;
    this.touch.scale = this.state.zoom;
    this.touch.pinchStartLen = null;
    this.touch.x1 = null;
    this.touch.x2 = null;
    this.touch.y1 = null;
    this.touch.y2 = null;
    this.touch.multiTouch = false;
    if (this.touch.pathID) {
      this.props.pathEnd(this.touch.pathID);
      this.touch.pathID = null;
    }
  }

  onTouchCancel() {
    clearTimeout(this.touch.singleTapTimeout);
    clearTimeout(this.touch.tapTimeout);
    clearTimeout(this.touch.longTapTimeout);
    clearTimeout(this.touch.swipeTimeout);
    if (this.touch.pathID) {
      this.props.pathEnd(this.touch.pathID);
      this.touch.pathID = null;
    }
  }

  onZoom({ e }) {
    const scale = this.state.zoom * (e.deltaY > 0 ? 0.95 : 1.05);
    this.zoom(scale, e.clientX, e.clientY);
  }

  onKeyDown(e) {
    const keyCode = 'which' in e ? e.which : e.keyCode;
    if (keyCode === 8) {
      // "Backspace"
      e.preventDefault();
      const s = this.state.textInput;
      if (s) {
        this.setState({ textInput: s.substring(0, s.length - 1) });
        this.props.textUpdate(this.state.textID, this.state.textInput);
      } else {
        this.deleteActive();
      }
    } else if (keyCode === 46) {
      // "Delete"
      e.preventDefault();
      this.deleteActive();
    } else if (keyCode === 37) {
      // "Left"
      this.setState(state => ({ offsetX: state.offsetX + 10 }));
    } else if (keyCode === 38) {
      // "Up"
      this.setState(state => ({ offsetY: state.offsetY + 10 }));
    } else if (keyCode === 39) {
      // "Right"
      this.setState(state => ({ offsetX: state.offsetX - 10 }));
    } else if (keyCode === 40) {
      // "Down"
      this.setState(state => ({ offsetY: state.offsetY - 10 }));
    } else if (keyCode === 33) {
      // "PageUp"
      this.zoom(this.state.zoom * 1.05, this.svgEl.clientWidth / 2, this.svgEl.clientHeight / 2);
    } else if (keyCode === 34) {
      // "PageDown"
      this.zoom(this.state.zoom * 0.95, this.svgEl.clientWidth / 2, this.svgEl.clientHeight / 2);
    } else if (keyCode === 35 || keyCode === 36) {
      // "End" / "Home"
      this.setState({ zoom: 1, offsetX: 0, offsetY: 0 });
    } else if (keyCode === 90 && (e.ctrlKey || e.metaKey)) {
      this.undo();
    } else if (keyCode === 89 && (e.ctrlKey || e.metaKey)) {
      this.redo();
    }
  }

  onKeyPress(e) {
    const keyCode = 'which' in e ? e.which : e.keyCode;
    if (keyCode !== 0 && keyCode !== 13 && !e.ctrlKey && !e.metaKey) {
      // anything but "Enter"
      e.preventDefault();
      const c = `${String.fromCharCode(keyCode)}`;
      if (!this.state.textInput) {
        this.setState(state => ({
          textInput: c,
          textID: this.props.textCreate({
            x: state.cursorX,
            y: state.cursorY,
            text: c,
          }),
        }));
      } else {
        this.setState(state => ({ textInput: state.textInput + c }));
        this.props.textUpdate(this.state.textID, this.state.textInput);
      }
    }
    this.setState({ lastKeyPressTime: new Date() });
  }

  zoom(scale, x, y) {
    const oldZoom = this.state.zoom;
    const newZoom = Math.min(Math.max(scale, 0.2), 5);
    this.setState(state => ({
      zoom: newZoom,
      offsetX: (((state.offsetX - x) * newZoom) / oldZoom) + x,
      offsetY: (((state.offsetY - y) * newZoom) / oldZoom) + y,
    }));
  }

  deleteActive() {
    Object.keys(this.active).forEach((id) => {
      if (this.active[id]) {
        this.props.remove(id);
      }
    });
    this.active = {};
    this.setState({
      canDraw: false,
      pathID: null,
      textInput: '',
      textID: null,
    });
    this.forceUpdate();
  }

  undo() {
    this.setState({
      canDraw: false,
      pathID: null,
      textInput: '',
      textID: null,
    });
    this.props.undo();
    this.forceUpdate();
  }

  redo() {
    this.setState({
      canDraw: false,
      pathID: null,
      textInput: '',
      textID: null,
    });
    this.props.redo();
    this.forceUpdate();
  }

  render() {
    const xy = (e) => {
      e.preventDefault();
      const x = (e.clientX - this.state.offsetX) / this.state.zoom;
      const y = (e.clientY - this.state.offsetY) / this.state.zoom;
      this.setState({ cursorX: e.clientX, cursorY: e.clientY });
      return { x, y, e };
    };
    const dist = 25 / this.state.zoom;
    const renderPath = (id, el) => {
      const active = !this.isTouchDevice
        && this.props.elements.nearby(this.state.cursorX, this.state.cursorY, id, dist);
      const line = (a, b) => {
        const lx = b.x - a.x;
        const ly = b.y - a.y;
        return {
          length: Math.sqrt((lx ** 2) + (ly ** 2)),
          angle: Math.atan2(ly, lx),
        };
      };
      const cp = (current, previous, next, reverse) => {
        const smoothing = 0.2;
        const p = previous || current;
        const n = next || current;
        const o = line(p, n);
        const angle = o.angle + (reverse ? Math.PI : 0);
        const length = o.length * smoothing;
        const x = current.x + (Math.cos(angle) * length);
        const y = current.y + (Math.sin(angle) * length);
        return [x, y];
      };
      const bezier = (p, i, a) => {
        const [cpsX, cpsY] = cp(a[i - 1], a[i - 2], p);
        const [cpeX, cpeY] = cp(p, a[i - 1], a[i + 1], true);
        return `C ${cpsX},${cpsY} ${cpeX},${cpeY} ${p.x},${p.y}`;
      };
      const l = el.points.reduce(
        (acc, p, i, a) => (i === 0 ? `M ${p.x} ${p.y}` : `${acc} ${bezier(p, i, a)}`),
        '',
      );
      this.active[id] = active;
      return (
        <path
          key={id}
          className={active ? 'active' : ''}
          d={`${l}`}
        />
      );
    };
    const renderText = (id, el) => {
      const active = !this.isTouchDevice
        && this.props.elements.nearby(this.state.cursorX, this.state.cursorY, id, dist);
      this.active[id] = active;
      return (
        <text key={id} x={el.position.x} y={el.position.y} className={active ? 'active' : ''}>
          {el.text}
        </text>
      );
    };
    return (
      <svg
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: '100%',
          height: '100%',
          outline: 'none',
        }}
        className="board"
        tabindex="0" // eslint-disable-line react/no-unknown-property
        onMouseMove={e => this.onMouseMove(xy(e))}
        onMouseDown={e => this.onMouseDown(xy(e))}
        onMouseUp={e => this.onMouseUp(xy(e))}
        onMouseLeave={e => this.onMouseUp(xy(e))}
        onWheel={e => this.onZoom(xy(e))}
        onTouchStart={e => this.onTouchStart(e)}
        onTouchMove={e => this.onTouchMove(e)}
        onTouchEnd={e => this.onTouchEnd(e)}
        onTouchCancel={e => this.onTouchCancel(e)}
        onKeyDown={e => this.onKeyDown(e)}
        onKeyPress={e => this.onKeyPress(e)}
        ref={(el) => { this.svgEl = el; this.props.onRef(el); }}
      >
        <g
          transform={`translate(${this.state.offsetX},${
            this.state.offsetY
          }) scale(${this.state.zoom})`}
        >
          {
            this.props.elements.map((el, id) => (
              el.stroke ? renderPath(id, el.stroke) : renderText(id, el.text)
            ))
          }
        </g>
      </svg>
    );
  }
}
